import { useState } from "react"

import { useInstallIntegration } from "@/api/useIntegrations"
import { Form, Password, Steps, Text } from "@/atoms"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { getDisplayMessage, getFieldErrors } from "@/helpers/error.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { AsanaWorkspaceSelect } from "@/pages/IntegrationsPage/components/molecules/AsanaWorkspaceSelect"
import { ConnectPageHeading } from "@/pages/IntegrationsPage/components/molecules/ConnectPageHeading"
import { IntegrationBreadcrumbHeader } from "@/pages/IntegrationsPage/components/molecules/IntegrationBreadcrumbHeader"
import { ConnectIntegrationFooter } from "@/pages/IntegrationsPage/components/pages/ConnectIntegrationPage/ConnectIntegrationFooter"
import {
  ASANA_TOKEN_FIELDS,
  getAsanaWorkspaces,
  toAsanaInstallPayload,
  toFieldErrors
} from "@/pages/IntegrationsPage/helpers/ticketing"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"

import styles from "./AsanaIntegrationPage.module.scss"

import type { AsanaWorkspace } from "@/api/useIntegrations.types"
import type { AsanaTokenFormValues } from "./AsanaIntegrationPage.types"

const FORM_ID = "asana-token-form"

export const AsanaIntegrationPage = () => {
  const vendor = INTEGRATION_VENDORS.asana
  const [, actions] = useIntegration({ vendor })
  const { form, t: tRoot, message, isDirty, onFieldsChange } = actions
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.asana"
  })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const [workspaces, setWorkspaces] = useState<AsanaWorkspace[]>([])
  const installer = useInstallIntegration({ vendor })
  const title = t("title")

  const onValuesChange = (changed: Partial<AsanaTokenFormValues>) => {
    if (!("token" in changed) || !workspaces.length) return
    setWorkspaces([])
    form.setFields([{ name: "workspaceGid", value: undefined, errors: [] }])
  }

  const onFinish = async (values: AsanaTokenFormValues) => {
    try {
      await installer.mutateAsync({ data: toAsanaInstallPayload(values) })
    } catch (error) {
      const choices = getAsanaWorkspaces(error)
      const displayMessage = getDisplayMessage(error)
      const fieldErrors = toFieldErrors(ASANA_TOKEN_FIELDS, {
        ...(choices.length && displayMessage
          ? { workspaceGid: displayMessage }
          : {}),
        ...getFieldErrors(error)
      })

      if (choices.length) setWorkspaces(choices)

      if (fieldErrors.length) form.setFields(fieldErrors)
      else message.error(displayMessage ?? t("messages.installFailed"))
      return
    }

    message.success(t("messages.success"))
    actions.backToIntegration(true)
  }

  return (
    <PageWithBreadcrumbTemplate
      full
      header={
        <IntegrationBreadcrumbHeader
          vendorName={tSystems("asana")}
          vendorUrl={actions.getBackToIntegrationUrl()}
          title={title}
        />
      }
      footer={
        <ConnectIntegrationFooter
          disabled={!isDirty}
          isLoading={installer.isPending}
          okProps={{ form: FORM_ID, htmlType: "submit" }}
          onCancel={() => actions.backToIntegration()}
        />
      }
    >
      <ConnectPageHeading title={title} />
      <Form
        form={form}
        id={FORM_ID}
        layout="vertical"
        onFieldsChange={onFieldsChange}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
      >
        <Steps
          className={styles.steps}
          size="small"
          orientation="vertical"
          items={[
            {
              status: "process",
              title: t("form.token.title"),
              content: (
                <>
                  <Form.Item
                    label={
                      <Text type="secondary">{t("form.token.label")}</Text>
                    }
                    colon={false}
                    extra={
                      <span className={styles.fieldDescription}>
                        {t("form.token.help")}
                      </span>
                    }
                    name="token"
                    required={false}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: tRoot("error.requiredField")
                      }
                    ]}
                  >
                    <Password
                      autoComplete="new-password"
                      placeholder={t("form.token.placeholder")}
                      visibilityToggle={false}
                    />
                  </Form.Item>
                  <Form.Item
                    hidden={!workspaces.length}
                    label={
                      <Text type="secondary">{t("form.workspace.label")}</Text>
                    }
                    colon={false}
                    name="workspaceGid"
                    required={false}
                    rules={[
                      {
                        required: !!workspaces.length,
                        message: tRoot("error.requiredField")
                      }
                    ]}
                  >
                    <AsanaWorkspaceSelect workspaces={workspaces} />
                  </Form.Item>
                </>
              )
            }
          ]}
        />
      </Form>
    </PageWithBreadcrumbTemplate>
  )
}
